import React from 'react';
import { Boxes, DollarSign, Home, Lightbulb, ScrollText, Truck, Users, BadgeDollarSign } from 'lucide-react';

/**
 * Компонент детализации расходов для калькулятора рентабельности
 */
const DetailedCostsSection = ({ 
  formData, 
  handleInputChange, 
  errors 
}) => {
  // Если опция детализации расходов отключена, не отображаем компонент
  if (!formData.includeDetailedCosts) return null;

  // Рассчитываем общую сумму детализированных расходов
  const calculateTotalDetailedCosts = () => {
    const parseAmount = (value) => {
      const parsed = parseFloat(String(value).replace(',', '.'));
      return isNaN(parsed) ? 0 : parsed;
    };

    return parseAmount(formData.materialCosts) +
           parseAmount(formData.laborCosts) +
           parseAmount(formData.rentCosts) +
           parseAmount(formData.utilitiesCosts) +
           parseAmount(formData.marketingCosts) +
           parseAmount(formData.logisticsCosts) +
           parseAmount(formData.adminCosts) +
           parseAmount(formData.otherCosts);
  };

  // Рассчитываем общую сумму из основных параметров
  const calculateTotalRegularCosts = () => {
    const parseAmount = (value) => {
      const parsed = parseFloat(String(value).replace(',', '.'));
      return isNaN(parsed) ? 0 : parsed;
    };

    return parseAmount(formData.costOfGoods) + parseAmount(formData.operatingExpenses);
  };

  const totalDetailedCosts = calculateTotalDetailedCosts();
  const totalRegularCosts = calculateTotalRegularCosts();
  const costsDiscrepancy = Math.abs(totalDetailedCosts - totalRegularCosts) > 1;

  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Boxes className="w-5 h-5 mr-2 text-blue-500" />
        Детализация расходов
      </h3>
      
      {/* Информационное сообщение об общей сумме */}
      <div className={`mb-4 p-3 rounded-lg ${costsDiscrepancy ? 'bg-yellow-100 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700' : 'bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800'}`}>
        <div className="flex items-center text-sm">
          <DollarSign className={`w-5 h-5 mr-2 ${costsDiscrepancy ? 'text-yellow-600 dark:text-yellow-400' : 'text-emerald-600 dark:text-emerald-400'}`} />
          <div>
            <p className={`font-medium ${costsDiscrepancy ? 'text-yellow-700 dark:text-yellow-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
              Общая сумма детализированных расходов: {totalDetailedCosts.toLocaleString('ru-RU')} ₽
            </p>
            <p className="text-xs mt-1">
              {costsDiscrepancy
                ? `Сумма не соответствует общим расходам (${totalRegularCosts.toLocaleString('ru-RU')} ₽). Скорректируйте значения.`
                : 'Сумма соответствует общим расходам, указанным в основных параметрах.'}
            </p>
          </div>
        </div>
      </div>

      {/* Отображение ошибки несоответствия сумм */}
      {errors.detailedCostsTotal && (
        <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-lg text-red-700 dark:text-red-300 text-sm">
          {errors.detailedCostsTotal}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Расходы на материалы */}
        <div>
          <label htmlFor="materialCosts" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Boxes className="w-4 h-4 mr-2 text-blue-500" />
            Материалы и комплектующие
          </label>
          <input
            type="text"
            id="materialCosts"
            name="materialCosts"
            value={formData.materialCosts}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.materialCosts ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.materialCosts && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.materialCosts}
            </p>
          )}
        </div>

        {/* Расходы на оплату труда */}
        <div>
          <label htmlFor="laborCosts" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Users className="w-4 h-4 mr-2 text-blue-500" />
            Оплата труда и страховые взносы
          </label>
          <input
            type="text"
            id="laborCosts"
            name="laborCosts"
            value={formData.laborCosts}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.laborCosts ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.laborCosts && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.laborCosts}
            </p>
          )}
        </div>

        {/* Расходы на аренду */}
        <div>
          <label htmlFor="rentCosts" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Home className="w-4 h-4 mr-2 text-blue-500" />
            Аренда помещений
          </label>
          <input
            type="text"
            id="rentCosts"
            name="rentCosts"
            value={formData.rentCosts}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.rentCosts ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.rentCosts && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.rentCosts}
            </p>
          )}
        </div>

        {/* Коммунальные платежи */}
        <div>
          <label htmlFor="utilitiesCosts" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Lightbulb className="w-4 h-4 mr-2 text-blue-500" />
            Коммунальные платежи
          </label>
          <input
            type="text"
            id="utilitiesCosts"
            name="utilitiesCosts"
            value={formData.utilitiesCosts}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.utilitiesCosts ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.utilitiesCosts && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.utilitiesCosts}
            </p>
          )}
        </div>

        {/* Маркетинг и реклама */}
        <div>
          <label htmlFor="marketingCosts" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <BadgeDollarSign className="w-4 h-4 mr-2 text-blue-500" />
            Маркетинг и реклама
          </label>
          <input
            type="text"
            id="marketingCosts"
            name="marketingCosts"
            value={formData.marketingCosts}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.marketingCosts ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.marketingCosts && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.marketingCosts}
            </p>
          )}
        </div>

        {/* Логистика и транспорт */}
        <div>
          <label htmlFor="logisticsCosts" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Truck className="w-4 h-4 mr-2 text-blue-500" />
            Логистика и транспорт
          </label>
          <input
            type="text"
            id="logisticsCosts"
            name="logisticsCosts"
            value={formData.logisticsCosts}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.logisticsCosts ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.logisticsCosts && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.logisticsCosts}
            </p>
          )}
        </div>

        {/* Административные расходы */}
        <div>
          <label htmlFor="adminCosts" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <ScrollText className="w-4 h-4 mr-2 text-blue-500" />
            Административные расходы
          </label>
          <input
            type="text"
            id="adminCosts"
            name="adminCosts"
            value={formData.adminCosts}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.adminCosts ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.adminCosts && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.adminCosts}
            </p>
          )}
        </div>

        {/* Прочие расходы */}
        <div>
          <label htmlFor="otherCosts" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <DollarSign className="w-4 h-4 mr-2 text-blue-500" />
            Прочие расходы
          </label>
          <input
            type="text"
            id="otherCosts"
            name="otherCosts"
            value={formData.otherCosts}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.otherCosts ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.otherCosts && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.otherCosts}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
        <p>
          Детализация расходов поможет лучше понять структуру затрат и выявить области для оптимизации.
          Сумма всех категорий должна соответствовать сумме себестоимости и операционных расходов, 
          указанных в основных параметрах.
        </p>
      </div>
    </div>
  );
};

export default DetailedCostsSection;